/**
 * Collections — per-unit detail page.
 *
 * Layout:
 *  - Header card: unit label, primary resident, contact info, copy pay-link button
 *  - Three tabs:
 *      Bills     — unpaid bills list (period / due / amount / late fee / total)
 *      Reminders — timeline of sent reminders (channel / template / status)
 *      Notes     — add/edit/resolve notes (textarea + follow-up date)
 *  - Quick action: "Mark contact attempt" → opens the notes tab + prefills body
 */
import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Bell,
  CheckCircle2,
  ClipboardCopy,
  FileText,
  HandCoins,
  Mail,
  MessageSquare,
  Phone,
  PhoneCall,
  Plus,
  Smartphone,
  StickyNote
} from 'lucide-react';
import { toast } from 'sonner';
import { get, patch, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { TabsPills } from '@/components/ui/tabs-pills';
import { formatDate, formatDateTime } from '@/lib/format';

type Channel = 'email' | 'sms' | 'whatsapp' | 'app_notification';

interface DetailResponse {
  unit: {
    id: string;
    number: string;
    block_name: string | null;
    label: string;
    residents: Array<{
      id: string;
      full_name: string;
      phone: string;
      email: string | null;
      is_primary: boolean;
      relation: string;
    }>;
  };
  unpaid_bills: Array<{
    id: string;
    period_label: string;
    amount_inr: number;
    late_fee_inr: number;
    due_date: string;
    status: string;
    created_at: string;
  }>;
  reminders: Array<{
    id: string;
    channel: Channel;
    sent_at: string;
    template_used: string | null;
    message_preview: string | null;
    delivery_status: string;
    delivery_error: string | null;
    bill_id: string | null;
  }>;
  notes: Array<{
    id: string;
    body: string;
    follow_up_date: string | null;
    resolved: boolean;
    created_at: string;
    updated_at: string;
    bill_id: string | null;
  }>;
}

const CHANNEL_LABEL: Record<Channel, string> = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  app_notification: 'In-app'
};

const CHANNEL_ICON: Record<Channel, React.ComponentType<{ className?: string }>> = {
  email: Mail,
  sms: MessageSquare,
  whatsapp: PhoneCall,
  app_notification: Smartphone
};

function inr(n: number): string {
  return `₹${(n ?? 0).toLocaleString('en-IN')}`;
}

export function CollectionDetailPage(): JSX.Element {
  const params = useParams({ strict: false }) as { unitId: string };
  const unitId = params.unitId;
  const qc = useQueryClient();
  const [tab, setTab] = useState<'bills' | 'reminders' | 'notes'>('bills');

  const detailQuery = useQuery({
    queryKey: ['collections', 'detail', unitId],
    queryFn: () => get<DetailResponse>(`/collections/defaulters/${unitId}`),
    enabled: Boolean(unitId)
  });

  // ─── Reminder ──────────────────────────────────────────────
  const [reminderOpen, setReminderOpen] = useState(false);
  const [channel, setChannel] = useState<Channel>('email');
  const [template, setTemplate] = useState<'gentle' | 'firm' | 'final' | 'custom'>('gentle');
  const [customMessage, setCustomMessage] = useState('');

  const reminderMutation = useMutation({
    mutationFn: () =>
      post('/collections/reminders', {
        unit_ids: [unitId],
        channel,
        template,
        ...(template === 'custom' ? { custom_message: customMessage.trim() } : {})
      }),
    onSuccess: () => {
      toast.success('Reminder logged');
      setReminderOpen(false);
      setCustomMessage('');
      void qc.invalidateQueries({ queryKey: ['collections', 'detail', unitId] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── Notes ─────────────────────────────────────────────────
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [noteFollowUp, setNoteFollowUp] = useState('');
  const [noteEditing, setNoteEditing] = useState<string | null>(null);

  const createNote = useMutation({
    mutationFn: () =>
      post('/collections/notes', {
        unit_id: unitId,
        body: noteBody.trim(),
        ...(noteFollowUp ? { follow_up_date: new Date(noteFollowUp).toISOString() } : {})
      }),
    onSuccess: () => {
      toast.success('Note added');
      setNoteOpen(false);
      setNoteBody('');
      setNoteFollowUp('');
      void qc.invalidateQueries({ queryKey: ['collections', 'detail', unitId] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const updateNote = useMutation({
    mutationFn: (vars: { id: string; body: string; follow_up_date: string | null }) =>
      patch(`/collections/notes/${vars.id}`, {
        body: vars.body,
        follow_up_date: vars.follow_up_date
      }),
    onSuccess: () => {
      toast.success('Note updated');
      setNoteEditing(null);
      void qc.invalidateQueries({ queryKey: ['collections', 'detail', unitId] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const resolveNote = useMutation({
    mutationFn: (id: string) => post(`/collections/notes/${id}/resolve`, {}),
    onSuccess: () => {
      toast.success('Note resolved');
      void qc.invalidateQueries({ queryKey: ['collections', 'detail', unitId] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  function quickContactAttempt(): void {
    setNoteBody('Contact attempt — ');
    setNoteFollowUp('');
    setNoteOpen(true);
    setTab('notes');
  }

  function copyPayLink(): void {
    const url = `https://app.hooddesk.com/pay/unit/${encodeURIComponent(unitId)}`;
    void navigator.clipboard.writeText(url).then(
      () => toast.success('Quick-pay link copied'),
      () => toast.error('Could not copy to clipboard')
    );
  }

  if (detailQuery.isLoading) {
    return (
      <div className="flex flex-col gap-vt-4">
        <PageHeader title="Loading unit…" description="Fetching defaulter detail" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="flex flex-col gap-vt-4">
        <PageHeader title="Unit not found" description="Could not load defaulter detail." />
        <Card>
          <CardContent className="p-vt-4">
            <Link to="/collections" className="text-vt-primary hover:underline">
              ← Back to collections
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const data = detailQuery.data;
  const primary =
    data.unit.residents.find((r) => r.is_primary) ?? data.unit.residents[0] ?? null;
  const totalOutstanding = data.unpaid_bills.reduce(
    (s, b) => s + (b.amount_inr ?? 0) + (b.late_fee_inr ?? 0),
    0
  );

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title={`Collections — ${data.unit.label}`}
        description="Unit-level bill history, reminder timeline and follow-up notes."
        actions={
          <div className="flex flex-wrap gap-vt-2">
            <Link
              to="/collections"
              className="inline-flex items-center gap-vt-1 rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-1 text-vt-13 text-vt-text-muted hover:bg-vt-surface-2"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Link>
            <Button size="sm" variant="secondary" onClick={copyPayLink}>
              <ClipboardCopy className="h-3.5 w-3.5" /> Copy pay link
            </Button>
            <Button size="sm" variant="secondary" onClick={quickContactAttempt}>
              <Phone className="h-3.5 w-3.5" /> Mark contact attempt
            </Button>
            <Button size="sm" onClick={() => setReminderOpen(true)}>
              <Bell className="h-3.5 w-3.5" /> Send reminder
            </Button>
          </div>
        }
      />

      {/* Header card — unit + resident */}
      <Card>
        <CardContent className="p-vt-4">
          <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-4">
            <Field
              label="Unit"
              value={
                <span className="text-vt-16 font-semibold text-vt-text">{data.unit.label}</span>
              }
            />
            <Field
              label="Primary resident"
              value={
                primary ? (
                  <span className="text-vt-text">{primary.full_name}</span>
                ) : (
                  <span className="text-vt-text-muted">No resident on file</span>
                )
              }
            />
            <Field
              label="Phone"
              value={
                primary ? (
                  <a
                    href={`tel:${primary.phone}`}
                    className="font-mono text-vt-text hover:underline"
                  >
                    {primary.phone}
                  </a>
                ) : (
                  '—'
                )
              }
            />
            <Field
              label="Total outstanding"
              value={
                <span className="font-mono text-vt-16 font-semibold text-vt-alert">
                  {inr(totalOutstanding)}
                </span>
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <TabsPills
        aria-label="Detail tabs"
        value={tab}
        onChange={(v) => setTab(v as 'bills' | 'reminders' | 'notes')}
        options={[
          { value: 'bills', label: 'Bills', count: data.unpaid_bills.length },
          { value: 'reminders', label: 'Reminders', count: data.reminders.length },
          { value: 'notes', label: 'Notes', count: data.notes.length }
        ]}
      />

      {tab === 'bills' ? (
        <Card>
          <CardContent className="p-0">
            {data.unpaid_bills.length === 0 ? (
              <EmptyState
                icon={<HandCoins className="h-7 w-7" strokeWidth={1.5} />}
                title="No outstanding bills"
                description="This unit currently has no unpaid maintenance bills."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Late fee</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.unpaid_bills.map((b) => {
                    const overdue = new Date(b.due_date).getTime() < Date.now();
                    return (
                      <TableRow key={b.id}>
                        <TableCell className="font-medium">{b.period_label}</TableCell>
                        <TableCell>
                          <Badge variant={overdue ? 'alert' : 'warn'}>
                            {overdue ? 'Overdue' : b.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-vt-12 text-vt-text-muted">
                          {formatDate(b.due_date)}
                        </TableCell>
                        <TableCell className="text-right font-mono">{inr(b.amount_inr)}</TableCell>
                        <TableCell className="text-right font-mono">{inr(b.late_fee_inr)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {inr((b.amount_inr ?? 0) + (b.late_fee_inr ?? 0))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : null}

      {tab === 'reminders' ? (
        <Card>
          <CardContent className="p-vt-4">
            {data.reminders.length === 0 ? (
              <EmptyState
                icon={<Bell className="h-7 w-7" strokeWidth={1.5} />}
                title="No reminders sent yet"
                description="Click 'Send reminder' above to log the first follow-up to this unit."
              />
            ) : (
              <ol className="relative flex flex-col gap-vt-3 border-l border-vt-border pl-vt-4">
                {data.reminders.map((r) => {
                  const Icon = CHANNEL_ICON[r.channel];
                  return (
                    <li key={r.id} className="relative">
                      <span className="absolute -left-[1.4rem] flex h-6 w-6 items-center justify-center rounded-full bg-vt-primary-100 text-vt-primary">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="flex flex-col gap-vt-1 rounded-vt-sm border border-vt-border bg-vt-surface p-vt-3">
                        <div className="flex flex-wrap items-center justify-between gap-vt-2">
                          <div className="flex items-center gap-vt-2">
                            <Badge variant="info">{CHANNEL_LABEL[r.channel]}</Badge>
                            {r.template_used ? (
                              <Badge variant="muted">{r.template_used}</Badge>
                            ) : null}
                            <Badge
                              variant={
                                r.delivery_status === 'delivered'
                                  ? 'ok'
                                  : r.delivery_status === 'failed'
                                    ? 'alert'
                                    : 'warn'
                              }
                            >
                              {r.delivery_status}
                            </Badge>
                          </div>
                          <span className="text-vt-12 text-vt-text-muted">
                            {formatDateTime(r.sent_at)}
                          </span>
                        </div>
                        {r.message_preview ? (
                          <p className="text-vt-13 text-vt-text">{r.message_preview}</p>
                        ) : null}
                        {r.delivery_error ? (
                          <p className="text-vt-12 text-vt-alert">
                            Delivery error: {r.delivery_error}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent>
        </Card>
      ) : null}

      {tab === 'notes' ? (
        <Card>
          <CardContent className="p-vt-4">
            <div className="mb-vt-3 flex items-center justify-between">
              <h3 className="text-vt-14 font-semibold text-vt-text">Notes</h3>
              <Button
                size="sm"
                onClick={() => {
                  setNoteBody('');
                  setNoteFollowUp('');
                  setNoteOpen(true);
                }}
              >
                <Plus className="h-3.5 w-3.5" /> Add note
              </Button>
            </div>
            {data.notes.length === 0 ? (
              <EmptyState
                icon={<StickyNote className="h-7 w-7" strokeWidth={1.5} />}
                title="No notes yet"
                description="Track contact attempts, promises-to-pay, follow-up dates and special instructions for this unit."
              />
            ) : (
              <div className="flex flex-col gap-vt-2">
                {data.notes.map((n) => (
                  <NoteRow
                    key={n.id}
                    note={n}
                    editing={noteEditing === n.id}
                    onEdit={() => setNoteEditing(n.id)}
                    onCancelEdit={() => setNoteEditing(null)}
                    onSaveEdit={(body, followUp) =>
                      updateNote.mutate({
                        id: n.id,
                        body,
                        follow_up_date: followUp ? new Date(followUp).toISOString() : null
                      })
                    }
                    onResolve={() => resolveNote.mutate(n.id)}
                    saving={updateNote.isPending}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Reminder dialog */}
      <Dialog
        open={reminderOpen}
        onOpenChange={(open) => {
          if (!open) {
            setReminderOpen(false);
            setCustomMessage('');
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send reminder to {data.unit.label}</DialogTitle>
            <DialogDescription>
              Logged for audit. Picked-channel + template define the message preview persisted.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-vt-3 py-vt-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="cd-channel">Channel</Label>
              <Select id="cd-channel" value={channel} onChange={(e) => setChannel(e.target.value as Channel)}>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="app_notification">App notification</option>
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="cd-template">Template</Label>
              <Select
                id="cd-template"
                value={template}
                onChange={(e) =>
                  setTemplate(e.target.value as 'gentle' | 'firm' | 'final' | 'custom')
                }
              >
                <option value="gentle">Gentle</option>
                <option value="firm">Firm</option>
                <option value="final">Final notice</option>
                <option value="custom">Custom message</option>
              </Select>
            </div>
            {template === 'custom' ? (
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="cd-custom">Custom message</Label>
                <Textarea
                  id="cd-custom"
                  rows={4}
                  maxLength={500}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Type the message to send (max 500 chars)…"
                />
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setReminderOpen(false)}
              disabled={reminderMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (template === 'custom' && !customMessage.trim()) {
                  toast.error('Custom message is required for the custom template');
                  return;
                }
                reminderMutation.mutate();
              }}
              loading={reminderMutation.isPending}
              loadingText="Sending…"
            >
              <Bell className="h-4 w-4" /> Send reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New-note dialog */}
      <Dialog
        open={noteOpen}
        onOpenChange={(open) => {
          if (!open) {
            setNoteOpen(false);
            setNoteBody('');
            setNoteFollowUp('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add collection note</DialogTitle>
            <DialogDescription>
              Notes are persisted against this unit. Use them to log contact attempts, promises-to-pay,
              and follow-up commitments.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-vt-3 py-vt-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="note-body">Body</Label>
              <Textarea
                id="note-body"
                rows={5}
                maxLength={2000}
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                placeholder="e.g. Called primary owner — promised to pay by 25th"
              />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="note-followup">Follow-up date (optional)</Label>
              <Input
                id="note-followup"
                type="date"
                value={noteFollowUp}
                onChange={(e) => setNoteFollowUp(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setNoteOpen(false)}
              disabled={createNote.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!noteBody.trim()) {
                  toast.error('Note body is required');
                  return;
                }
                createNote.mutate();
              }}
              loading={createNote.isPending}
              loadingText="Saving…"
            >
              <Plus className="h-4 w-4" /> Add note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-vt-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-vt-text-soft">
        {label}
      </span>
      <span className="text-vt-13">{value}</span>
    </div>
  );
}

function NoteRow({
  note,
  editing,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onResolve,
  saving
}: {
  note: {
    id: string;
    body: string;
    follow_up_date: string | null;
    resolved: boolean;
    created_at: string;
    updated_at: string;
  };
  editing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (body: string, followUp: string) => void;
  onResolve: () => void;
  saving: boolean;
}): JSX.Element {
  const [body, setBody] = useState(note.body);
  const [followUp, setFollowUp] = useState<string>(
    note.follow_up_date ? note.follow_up_date.slice(0, 10) : ''
  );
  return (
    <div
      className={`flex flex-col gap-vt-2 rounded-vt-sm border bg-vt-surface p-vt-3 ${
        note.resolved ? 'border-vt-border opacity-70' : 'border-vt-border'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-vt-2">
        <div className="flex items-center gap-vt-2">
          <Badge variant={note.resolved ? 'ok' : 'warn'}>
            {note.resolved ? 'Resolved' : 'Open'}
          </Badge>
          {note.follow_up_date ? (
            <Badge variant="info">Follow up {formatDate(note.follow_up_date)}</Badge>
          ) : null}
        </div>
        <span className="text-vt-11 text-vt-text-muted">
          Updated {formatDateTime(note.updated_at)}
        </span>
      </div>
      {editing ? (
        <>
          <Textarea
            rows={3}
            maxLength={2000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="flex items-end justify-between gap-vt-2">
            <div className="flex flex-col gap-vt-1">
              <Label className="text-vt-12 text-vt-text-muted">Follow-up</Label>
              <Input
                type="date"
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
              />
            </div>
            <div className="flex gap-vt-2">
              <Button size="sm" variant="ghost" onClick={onCancelEdit} disabled={saving}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => onSaveEdit(body.trim(), followUp)}
                disabled={!body.trim() || saving}
              >
                <FileText className="h-3.5 w-3.5" /> Save
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <p className="whitespace-pre-wrap text-vt-13 text-vt-text">{note.body}</p>
          <div className="flex justify-end gap-vt-1">
            {!note.resolved ? (
              <>
                <Button size="sm" variant="ghost" onClick={onEdit}>
                  Edit
                </Button>
                <Button size="sm" variant="secondary" onClick={onResolve}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Mark resolved
                </Button>
              </>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
